/**
 * LIGHT PATHS (B4): what the LightLayer draws, built once from the data.
 *
 *   routePaths(routes)        one kind "route" path per RouteRecord (id = place id)
 *   sitePath(site, origin)    the site outline as a closed ring, rotated to start at the vertex nearest the
 *                              route origin (the light leaves the site where the routes leave it) — spec §D.4
 *   routeFor(place, routes)   the RouteRecord of a place (place.route.routeId)
 *   corridorCoords(fc, id)    a corridor's vertices (roads / future), for framing
 */
import type { CorridorCollection, LngLat, Place, RouteRecord, RoutesFile, SiteCollection } from "@/contracts/data";
import type { LightPath } from "@/contracts/map";
import { cumulative, haversine } from "@/lib/geo";

export const SITE_PATH_ID = "site-outline";

export function routePath(r: RouteRecord): LightPath {
  return { id: r.id, coords: r.coords as LngLat[], cum: r.cum, lengthM: r.lengthM, kind: "route" };
}

export function routePaths(routes: RoutesFile | null | undefined): LightPath[] {
  return routes ? routes.routes.map(routePath) : [];
}

/** The site polygon's outer ring as a closed LightPath starting at the vertex nearest `origin`. */
export function sitePath(site: SiteCollection | null | undefined, origin: LngLat | null): LightPath | null {
  const poly = site?.features.find((f) => f.geometry.type === "Polygon");
  if (!poly || poly.geometry.type !== "Polygon") return null;
  let ring = poly.geometry.coordinates[0].map((c) => [c[0], c[1]] as LngLat);
  if (ring.length < 3) return null;
  const a = ring[0];
  const z = ring[ring.length - 1];
  if (a[0] === z[0] && a[1] === z[1]) ring = ring.slice(0, -1);
  let k = 0;
  if (origin) {
    let best = Infinity;
    ring.forEach((c, i) => {
      const d = haversine(c, origin);
      if (d < best) {
        best = d;
        k = i;
      }
    });
  }
  const coords = [...ring.slice(k), ...ring.slice(0, k), ring[k]];
  const cum = cumulative(coords);
  return { id: SITE_PATH_ID, coords, cum, lengthM: cum[cum.length - 1], kind: "site", closed: true };
}

export function routeFor(place: Place | null | undefined, routes: RoutesFile | null | undefined): RouteRecord | null {
  if (!place || !routes) return null;
  const id = place.route?.routeId ?? place.id;
  return routes.routes.find((r) => r.id === id) ?? null;
}

/** All vertices of a corridor feature (LineString or MultiLineString), or [] when it is missing. */
export function corridorCoords(fc: CorridorCollection | null | undefined, id: string | null | undefined): LngLat[] {
  if (!fc || !id) return [];
  const f = fc.features.find((x) => x.properties.id === id || x.id === id);
  if (!f) return [];
  const g = f.geometry;
  return (g.type === "LineString" ? g.coordinates : g.coordinates.flat()) as LngLat[];
}

/** Every `step`-th vertex plus the last: enough for framing, cheap to project. */
export function thin(coords: readonly LngLat[], step: number): LngLat[] {
  if (step <= 1 || coords.length <= 2) return [...coords];
  const out: LngLat[] = [];
  for (let i = 0; i < coords.length; i += step) out.push(coords[i]);
  const last = coords[coords.length - 1];
  if (out[out.length - 1] !== last) out.push(last);
  return out;
}

/** The coarse LOD of a route (lod1 indices), for bloom framing. */
export function lodCoords(r: RouteRecord): LngLat[] {
  const idx = r.lod1?.length ? r.lod1 : r.coords.map((_, i) => i);
  return idx.map((i) => r.coords[i] as LngLat).filter(Boolean);
}
